import React, { useState, useEffect } from 'react';
import Icon from '_Components/Icons/Icon';
import { useQuery } from 'react-apollo';
import GENERATE_IRRITANTS from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/generateIrritants';
import QcardHeader from '_Components/QcardPanel/QcardHeader/QcardHeader';
import CModal from '_Components/CModal/CModal';
import Loader from '_Components/Loader/OnLoad';
import IrritantsAndEnchantingPointsAIInsightsTable from './IrritantsAndEnchantingPointsAIInsightsTable';

type ThematicType = {
  thematic: string;
  subThematic: string | null;
  subSubThematic: string | null;
};

type IrritantsAndEnchantingPointsAIModalType = {
  isOpen: boolean;
  thematicAnalyzed: ThematicType;
  insight: string;
  icon: string;
  iconColor: string;
  generationText: any;
  t: Function;
  projectId: number;
  range: { startDate: Date; endDate: Date };
  handleFetchClick: Function;
  shouldFetch: boolean;
  setShouldFetch: Function;
  numberOfMentions: number;
  title: string;
  titleSecondPart: string;
  className: string;
  toggle: Function;
  modal: boolean;
  queryFilters: any;
};

const IrritantsAndEnchantingPointsAIModal = ({
  isOpen,
  thematicAnalyzed,
  insight,
  icon,
  iconColor,
  generationText,
  t,
  projectId,
  range,
  handleFetchClick,
  shouldFetch,
  setShouldFetch,
  numberOfMentions,
  title,
  titleSecondPart,
  className,
  toggle,
  modal,
  queryFilters,
  ...props
}: IrritantsAndEnchantingPointsAIModalType) => {
  const [AIData, setAIData] = useState([]);
  const { criteriaKeys, dataCollectorId, keywords, selectedEmotions, selectedSentiments, thematics } = queryFilters;

  const { data: irritantsData, loading: loadingIrritants, error: errorIrritants } = useQuery(GENERATE_IRRITANTS, {
    variables: {
      projectId,
      range,
      criteriaKeys,
      thematics,
      keywords,
      emotions: selectedEmotions,
      sentiments: selectedSentiments,
      dataCollectorId,
      thematicAnalyzed,
      insight,
    },
    skip: !shouldFetch,
  });

  useEffect(() => {
    if (isOpen && irritantsData !== undefined) {
      setAIData(irritantsData?.generateIrritants);
    }
  }, [isOpen, irritantsData]);

  const customHeader = (
    <div>
      <div style={{ display: 'inline-block', position: 'absolute', top: '16px', left: '24px' }}>
        <img
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADIElEQVR4nO1YTWtTQRSdIvhRFesXWAuCgpuCCIILa2ptmryZV2MbKmntyn0hNu9OgsVNduq+/ooutCtBLLyZebEgBKTqyv6F6i+wRG7bpM/0tXkfk5DIO3Ah0MzknNt775wZQmLEiOELJSpGOVObGMCcBOk1AJUbnKkaBn4mvQRuymSdfD0sQ4yTXgEwtdosgFP5nvQCwBBpoHL7oAD1p0idFOlWWIYYxyx7kt/vhW38TleVU4mKUXfD+g1cg2t9/UZK3ARTZcFQmZe0OqhVwN6orIWMzVb7Lz1yznOmngJV8xhFo/Ikzz6c0CYAmPoZVgCubbW/ZVTG6+RhLwpU3tEowEmELaFWB1yBysFm8oBhyLnSyOpZ0t1NXOuzDMU8BdAdESOkHcBRiSPTa4wCUxN+9ylMfLpxKHmKYc9Dxr7UFhGYZY/sv/O7PpdbOVagcvpoAWo+SEKCl9OB/4Dz0O96ztRwS/J0N6zk2lBXmbml7JeLnDqzfgWAqbLlTLVfvwDmJILYaSwbzHwg8nQ3OBUzPC2vY+OTzqPW9zzlXOPMeRyUODRFiVUmca+OUS9O2leOHJUhg6dlEk/wthF/kaqe45PygW7i0DRmi6a6vzBmn9FKHo2Z29u0OzgVs3iiaxMAhp3tFHloiPA/wn0IUJlOCwCdjwqlVOUqWuLOCRBTi9P2ANGJcu7HcctwboeZ+eA/6znLFLfwfNFK/h8hmWo/Z+Ku1qY25Rzu+WzMPkk6BctYv8DNz0kdY7MU9p6QZ+oyMPWdU/UNP4cSklwb2rn7Bp0ypjAxCSQKgMo3Lu//OlJZUTEThPxKrhatzq3c+ilgasvlPn9HcYhozPyWjRU180ieM/XW48K+jH8LtyteJ+3WHsmIcK3EOsdScWfeQ8QWfidMTxx6oacN8nPlKD5np2EDvECE+Q2vJxXQ9bSC06bdAhanvw54nRFFHY9b7S6hhggq7h2YPEwNE13Ym0DLept4Hzz98XSBCpflEFPa7UHzGOVM/tJ50caGRksMzEloN2Z1YKm4sv+K9BryaCXwvZPKjSg1HyNGjBj/N/4CHLpRmzINzWUAAAAASUVORK5CYII="
          alt="sparkling--v1"
        />
      </div>
      <div style={{ display: 'inline-block', position: 'absolute', top: '16px', right: '24px' }}>
        <Icon icon={icon} size={48} color={iconColor} />
        <b style={{ position: 'relative', top: '-1.3em', fontSize: '-1.5em' }}>{t('card:AI')}</b>
      </div>
      <QcardHeader {...props} numberOfMentions={numberOfMentions} title={title} titleSecondPart={titleSecondPart} />
    </div>
  );

  return (
    <CModal
      {...props}
      size="xl"
      customHeader={customHeader}
      title={title}
      titleSecondPart={titleSecondPart}
      className={className}
      toggle={toggle}
      modal={modal}
    >
      {loadingIrritants ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <div>
            <Icon icon={icon} size={48} color={iconColor} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p style={{ textAlign: 'center' }}>
              <h2>{generationText}</h2>
            </p>
            <p style={{ textAlign: 'center' }}>{t('card:Ai analysis explain')}</p>

            <Loader icon="INTERFACE_LOADING" />
            <p style={{ textAlign: 'center', width: '100%' }}>{t('card:Loading')}</p>
          </div>
        </div>
      ) : (
        <IrritantsAndEnchantingPointsAIInsightsTable data={AIData} insight={insight} t={t} />
      )}
    </CModal>
  );
};

export default IrritantsAndEnchantingPointsAIModal;
